import { useState } from 'react';
import { AlertCircle, Users } from 'react-feather';
import { API_BASE_URL } from '../../config/api';

/* Pending and connected Microsoft accounts.
 *
 * The point of this card is the pending list. When someone from the directory
 * signs in and auto-provisioning is off, or their Microsoft account does not
 * obviously belong to anyone here, the backend parks them as PENDING rather
 * than guessing. An admin then says which existing person they are, so their
 * agents and knowledge bases follow them instead of a second, empty account
 * appearing beside the original.
 *
 * The server offers a suggested match computed from email. It is pre-selected
 * for convenience but never applied on its own: an admin of a linked directory
 * controls every mailbox and UPN inside it, so email alone would be an account
 * takeover. The click is the authorization.
 *
 * Four things an admin can do to a row, and the reason each exists:
 *
 *   Connect, point the identity at an existing person.
 *   Create, the escape hatch when the dropdown has nobody to point at.
 *                  Lives *inside* the same dropdown as its last option, so the
 *                  admin discovers it at the moment they find no match rather
 *                  than having to know another page exists.
 *   Change, re-point a connected identity at someone else. The repair
 *                  for auto-provisioning having minted a duplicate account
 *                  before the admin could say who the person really was.
 *   Remove/Delete, revoke takes away Microsoft sign-in and keeps the account;
 *                  delete removes the account itself. Two different intentions,
 *                  so two buttons rather than one ambiguous "remove".
 *
 * Pending state is per row (`busyId`), never a shared boolean, with one flag
 * every row in the list disables while any single one is saving.
 */

const CREATE = '__create__';

const IdentitiesCard = ({ identities, users, authHeaders, onChanged }) => {
  // Which user each row is currently pointed at, keyed by identity id.
  // Seeded from the server's suggestion on first render of each row.
  const [choice, setChoice] = useState({});
  // Rows switched into "change who this is" mode, keyed by identity id.
  const [editing, setEditing] = useState({});
  // The new-account form, keyed by identity id, present only while open.
  const [draft, setDraft] = useState({});
  // The destructive action awaiting confirmation, as { id, kind }. One piece of
  // state for all of them: only ever one confirmation is open, and a second
  // flag per action drifts out of sync the moment a third one is added.
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const byId = new Map(users.map((u) => [u.id, u]));
  const pending = identities.filter((i) => i.status === 'PENDING');
  const active = identities.filter((i) => i.status === 'ACTIVE');
  const blocked = identities.filter((i) => i.status === 'BLOCKED');

  const call = async (identity, path, body, method = 'POST') => {
    if (busyId !== null) return;
    setBusyId(identity.id);
    setError('');

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/sso/identities/${identity.id}/${path}`,
        {
          method,
          headers: authHeaders(),
          ...(method === 'DELETE' ? {} : { body: JSON.stringify(body || {}) })
        }
      );
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(
          data?.detail || data?.error?.message || 'Could not update that person.'
        );
      }

      // Close whatever was open on this row; the reload replaces its data.
      setEditing((e) => ({ ...e, [identity.id]: false }));
      setDraft((d) => {
        const next = { ...d };
        delete next[identity.id];
        return next;
      });
      setConfirm(null);
      await onChanged();
    } catch (err) {
      setError(err.message || 'Could not update that person.');
    } finally {
      setBusyId(null);
    }
  };

  const nameOf = (identity) =>
    identity.display_name || identity.upn || identity.object_id;

  // The dropdown shared by the pending rows and the "change" mode of a
  // connected row. `exclude` keeps a connected row from offering the person it
  // is already pointed at as a change target.
  const renderPicker = (identity, selected, busy, exclude) => (
    <>
      <label className="sso-visually-hidden" htmlFor={`sso-map-${identity.id}`}>
        Connect {nameOf(identity)} to a person
      </label>
      <select
        id={`sso-map-${identity.id}`}
        className="sso-setting-select"
        value={selected}
        disabled={busy}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === CREATE) {
            // Opening the form seeds the email from the UPN. It is the best
            // guess available and is usually right, but it stays editable:
            // a UPN is not an email address and the two differ often enough
            // (aliases, vanity domains) that committing it silently would
            // create an account nobody recognises.
            setDraft((d) => ({
              ...d,
              [identity.id]: {
                email: identity.upn || '',
                contact_name: identity.display_name || '',
                role: 'MEMBER'
              }
            }));
            return;
          }
          setDraft((d) => {
            const next = { ...d };
            delete next[identity.id];
            return next;
          });
          setChoice((c) => ({
            ...c,
            [identity.id]: raw === '' ? '' : Number(raw)
          }));
        }}
      >
        <option value="">Choose a person…</option>
        {users
          .filter((u) => u.id !== exclude)
          .map((u) => (
            <option key={u.id} value={u.id}>
              {u.contact_name ? `${u.contact_name} (${u.email})` : u.email}
            </option>
          ))}
        <option value={CREATE}>+ Create a new account…</option>
      </select>
    </>
  );

  // The inline new-account form. Shown in place of the Connect button once
  // "Create a new account" is picked, so the decision and the detail stay on
  // the one row the admin is looking at.
  const renderCreateForm = (identity, form, busy) => {
    const emailOk = form.email.includes('@');

    return (
      <div className="sso-create-form">
        <div className="sso-create-fields">
          <label
            className="sso-visually-hidden"
            htmlFor={`sso-email-${identity.id}`}
          >
            Email address for the new account
          </label>
          <input
            id={`sso-email-${identity.id}`}
            className="sso-setting-select sso-create-input"
            type="email"
            placeholder="Email address"
            value={form.email}
            disabled={busy}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                [identity.id]: { ...form, email: e.target.value }
              }))
            }
          />

          <label
            className="sso-visually-hidden"
            htmlFor={`sso-name-${identity.id}`}
          >
            Full name for the new account
          </label>
          <input
            id={`sso-name-${identity.id}`}
            className="sso-setting-select sso-create-input"
            type="text"
            placeholder="Full name (optional)"
            value={form.contact_name}
            disabled={busy}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                [identity.id]: { ...form, contact_name: e.target.value }
              }))
            }
          />

          <label
            className="sso-visually-hidden"
            htmlFor={`sso-role-${identity.id}`}
          >
            Role for the new account
          </label>
          <select
            id={`sso-role-${identity.id}`}
            className="sso-setting-select"
            value={form.role}
            disabled={busy}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                [identity.id]: { ...form, role: e.target.value }
              }))
            }
          >
            <option value="MEMBER">Member</option>
            <option value="ADMIN">Administrator</option>
          </select>
        </div>

        <div className="sso-identity-actions">
          <button
            type="button"
            className="primary-button sso-row-button"
            disabled={busy || !emailOk}
            onClick={() =>
              call(identity, 'create-user', {
                email: form.email.trim(),
                contact_name: form.contact_name.trim() || null,
                role: form.role
              })
            }
          >
            {busy ? 'Creating…' : 'Create and connect'}
          </button>
          <button
            type="button"
            className="secondary-button sso-row-button"
            disabled={busy}
            onClick={() =>
              setDraft((d) => {
                const next = { ...d };
                delete next[identity.id];
                return next;
              })
            }
          >
            Cancel
          </button>
        </div>

        <p className="sso-create-hint">
          Signs in with Microsoft only, no password is set.
        </p>
      </div>
    );
  };

  // What each destructive action says before it runs, and what it does. The
  // wording is deliberately different per action: "are you sure?" tells an
  // admin nothing, whereas naming the person and the consequence is what
  // catches the click on the wrong row, the actual accident being guarded
  // against, since the list is a column of similar-looking names.
  const confirmSpec = (identity, kind, who) => {
    const specs = {
      deny: {
        title: `Deny ${who}?`,
        body: 'They will be blocked from signing in with Microsoft. They will not show up here again if they try, so you will need to assign them yourself to let them in later.',
        action: 'Deny access',
        danger: true,
        run: () => call(identity, 'revoke')
      },
      revoke: {
        title: `Remove Microsoft access for ${who}?`,
        body: 'They will be blocked from signing in with Microsoft from now on, and will not appear in the pending list if they try. An admin has to assign them again to let them back in. Their account and their work stay exactly as they are.',
        action: 'Remove access',
        danger: true,
        run: () => call(identity, 'revoke')
      },
      delete: {
        title: `Delete ${who} and everything in it?`,
        body: 'Their agents and knowledge bases go too. This cannot be undone.',
        action: 'Delete permanently',
        danger: true,
        run: () => call(identity, 'user', null, 'DELETE')
      }
    };
    return specs[kind];
  };

  const renderConfirm = (identity, kind, who, busy) => {
    const spec = confirmSpec(identity, kind, who);
    if (!spec) return null;

    return (
      <div className={`sso-confirm${spec.danger ? ' is-danger' : ''}`} role="alert">
        <p className="sso-confirm-title">{spec.title}</p>
        <p className="sso-confirm-text">{spec.body}</p>
        <div className="sso-identity-actions">
          <button
            type="button"
            className={`primary-button sso-row-button${
              spec.danger ? ' sso-danger-button' : ''
            }`}
            disabled={busy}
            onClick={spec.run}
          >
            {busy ? 'Working…' : spec.action}
          </button>
          <button
            type="button"
            className="secondary-button sso-row-button"
            disabled={busy}
            onClick={() => setConfirm(null)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderPending = (identity) => {
    // `?? suggested_user_id` and not `||`: user id 0 is not a real id here, but
    // relying on falsiness would also swallow a deliberate "" (no selection).
    const selected =
      choice[identity.id] !== undefined
        ? choice[identity.id]
        : identity.suggested_user_id ?? '';
    const busy = busyId === identity.id;
    const form = draft[identity.id];
    const confirming = confirm?.id === identity.id ? confirm.kind : null;

    return (
      <li key={identity.id} className="sso-identity-row is-pending">
        <div className="sso-identity-who">
          <span className="sso-identity-name">{nameOf(identity)}</span>
          {identity.upn && identity.display_name && (
            <span className="sso-identity-upn">{identity.upn}</span>
          )}
        </div>

        {!confirming && (
          <div className="sso-identity-actions">
            {renderPicker(identity, form ? CREATE : selected, busy)}

            {!form && (
              <>
                <button
                  type="button"
                  className="primary-button sso-row-button"
                  disabled={busy || selected === ''}
                  onClick={() => call(identity, 'assign', { user_id: selected })}
                >
                  {busy ? 'Saving…' : 'Connect'}
                </button>

                <button
                  type="button"
                  className="secondary-button sso-row-button"
                  disabled={busy}
                  onClick={() => setConfirm({ id: identity.id, kind: 'deny' })}
                >
                  Deny
                </button>
              </>
            )}
          </div>
        )}

        {confirming && renderConfirm(identity, confirming, nameOf(identity), busy)}
        {!confirming && form && renderCreateForm(identity, form, busy)}
      </li>
    );
  };

  const renderSettled = (identity) => {
    const user = identity.user_id ? byId.get(identity.user_id) : null;
    const busy = busyId === identity.id;
    const isEditing = Boolean(editing[identity.id]);
    const form = draft[identity.id];
    const selected = choice[identity.id] !== undefined ? choice[identity.id] : '';
    const confirming = confirm?.id === identity.id ? confirm.kind : null;
    // Name the account in the warning, not the directory display name: "Remove
    // Microsoft access for jane@contoso.com" is what lets an admin catch the
    // wrong row, since that is the thing they were choosing between.
    const who = user?.email || identity.user_email || nameOf(identity);

    // Only an SSO-only account is deletable from here. One with a password is a
    // full portal account that predates SSO; the endpoint refuses it, so the
    // button is not offered. `user_is_sso_only` is null for an unbound row.
    const deletable =
      identity.status === 'ACTIVE' && identity.user_is_sso_only === true;

    return (
      <li key={identity.id} className="sso-identity-row">
        <div className="sso-identity-who">
          <span className="sso-identity-name">{nameOf(identity)}</span>
          <span className="sso-identity-upn">
            {identity.status === 'BLOCKED'
              ? 'Denied, this account cannot sign in'
              : user
                ? `Signs in as ${user.email}`
                : identity.user_email
                  ? `Signs in as ${identity.user_email}`
                  : 'Connected'}
          </span>
        </div>

        {/* A denied row is not a dead end: assigning it a person sets it back
            to ACTIVE, which is the only way back in for someone who was
            denied, since they never reappear in the pending list. */}
        {identity.status === 'BLOCKED' && !isEditing && !confirming && (
          <div className="sso-identity-actions">
            <button
              type="button"
              className="secondary-button sso-row-button"
              disabled={busy}
              onClick={() => setEditing((e) => ({ ...e, [identity.id]: true }))}
            >
              Let them back in
            </button>
          </div>
        )}

        {identity.status === 'ACTIVE' && !isEditing && !confirming && (
          <div className="sso-identity-actions">
            <button
              type="button"
              className="secondary-button sso-row-button"
              disabled={busy}
              onClick={() => setEditing((e) => ({ ...e, [identity.id]: true }))}
            >
              Change person
            </button>
            <button
              type="button"
              className="secondary-button sso-row-button sso-danger-button"
              disabled={busy}
              onClick={() => setConfirm({ id: identity.id, kind: 'revoke' })}
            >
              Remove access
            </button>
            {deletable && (
              <button
                type="button"
                className="secondary-button sso-row-button sso-danger-button"
                disabled={busy}
                onClick={() => setConfirm({ id: identity.id, kind: 'delete' })}
              >
                Delete account
              </button>
            )}
          </div>
        )}

        {(identity.status === 'ACTIVE' || identity.status === 'BLOCKED') &&
          isEditing && (
            <div className="sso-identity-actions">
              {renderPicker(
                identity,
                form ? CREATE : selected,
                busy,
                identity.user_id
              )}
              {!form && (
                <>
                  <button
                    type="button"
                    className="primary-button sso-row-button"
                    disabled={busy || selected === ''}
                    onClick={() =>
                      call(identity, 'assign', { user_id: selected })
                    }
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="secondary-button sso-row-button"
                    disabled={busy}
                    onClick={() =>
                      setEditing((e) => ({ ...e, [identity.id]: false }))
                    }
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}

        {/* Every destructive action states who it affects and what happens,
            in the row itself, rather than relying on a browser confirm() the
            admin clicks through by reflex. */}
        {confirming && renderConfirm(identity, confirming, who, busy)}

        {!confirming && isEditing && form && renderCreateForm(identity, form, busy)}
      </li>
    );
  };

  return (
    <section className="account-section-card">
      <div className="account-section-header">
        <span className="account-section-icon" aria-hidden="true">
          <Users />
        </span>
        <div className="account-section-heading">
          <h2 className="account-section-title">Microsoft accounts</h2>
          <p className="account-section-description">
            People from your directory who have signed in, and who they are here.
          </p>
        </div>
      </div>

      <div className="account-section-body">
        {error && (
          <div className="account-banner error" role="alert">
            <AlertCircle aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {identities.length === 0 && (
          <p className="account-callout-text">
            Nobody has signed in with Microsoft yet. As people do, they appear
            here.
          </p>
        )}

        {pending.length > 0 && (
          <>
            <h3 className="sso-list-heading">
              Waiting for approval ({pending.length})
            </h3>
            <p className="account-callout-text sso-list-hint">
              Connect each person to their existing account so their work stays
              with them, or create a new account if they do not have one. Check
              the name against someone you know before you do.
            </p>
            <ul className="sso-identity-list">{pending.map(renderPending)}</ul>
          </>
        )}

        {active.length > 0 && (
          <>
            <h3 className="sso-list-heading">Connected ({active.length})</h3>
            <ul className="sso-identity-list">{active.map(renderSettled)}</ul>
          </>
        )}

        {blocked.length > 0 && (
          <>
            <h3 className="sso-list-heading">Denied ({blocked.length})</h3>
            <ul className="sso-identity-list">{blocked.map(renderSettled)}</ul>
          </>
        )}
      </div>
    </section>
  );
};

export default IdentitiesCard;
